import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Tooltip } from 'antd'
import { rubricsApi } from '@edulastic/api'
import { notification } from '@edulastic/common'
import { IconEye } from '@edulastic/icons'
import GradingRubricModal from '../../../assessment/containers/WidgetOptions/components/GradingRubricModal'
import { updateRubricDataAction } from '../../GradingRubric/ducks'
import {
  getBulkSuggestionsSelector,
  setBulkSuggestionsAction,
} from '../ducks/bulkQuestionMetadata'
import {
  applyPearAssistRubricToQuestion,
  removePearAssistRubricFromQuestion,
  unwrapRubric,
  findQuestionInTestItems,
} from '../utils/rubricApply'
import { getTestItemsSelector } from '../../TestPage/ducks'
import { StyledRubricActionButton } from './styled'

/**
 * Open the same "Use Existing Rubric" browse modal as item-details Scoring.
 * Use/Remove apply only to the item/question the modal was opened for
 * (suggestion-only until Save).
 */
export const useRubricBrowseModal = ({
  updateRubricData,
  setBulkSuggestions,
  testItems,
  suggestionsByQuestionId,
  onDeleteRubric,
}) => {
  const [loading, setLoading] = useState(false)
  const [showBrowseModal, setShowBrowseModal] = useState(false)
  const [target, setTarget] = useState(null) // { itemId, questionId }

  const reviewTargetQuestion = useMemo(
    () =>
      findQuestionInTestItems(testItems, target?.itemId, target?.questionId),
    [testItems, target]
  )

  const reviewTargetAttachedRubricId = useMemo(() => {
    if (!target?.questionId) return null
    return suggestionsByQuestionId?.[target.questionId]?.rubricId || null
  }, [target, suggestionsByQuestionId])

  const closeBrowseModal = useCallback(() => {
    setShowBrowseModal(false)
    setTarget(null)
    if (typeof updateRubricData === 'function') {
      updateRubricData(null)
    }
  }, [updateRubricData])

  const seedSuggestions = useCallback(
    (seededSuggestions) => {
      if (
        Object.keys(seededSuggestions || {}).length &&
        typeof setBulkSuggestions === 'function'
      ) {
        setBulkSuggestions(seededSuggestions)
      }
    },
    [setBulkSuggestions]
  )

  const handleReviewUseRubric = useCallback(
    async (rubric) => {
      if (!target?.itemId || !target?.questionId) return
      try {
        const result = await applyPearAssistRubricToQuestion({
          rubricOrId: rubric,
          itemId: target.itemId,
          questionId: target.questionId,
          suggestionsByQuestionId,
        })
        if (!result.applied) return
        seedSuggestions(result.seededSuggestions)
      } catch (err) {
        notification({
          type: 'warning',
          msg:
            err?.code === 'RUBRIC_CRITERIA_MISSING'
              ? 'Selected rubric has no criteria and cannot be applied.'
              : 'Unable to apply rubric. Please try again.',
        })
        throw err
      }
    },
    [target, suggestionsByQuestionId, seedSuggestions]
  )

  const handleReviewRemoveRubric = useCallback(() => {
    if (!target?.itemId || !target?.questionId) return
    const result = removePearAssistRubricFromQuestion({
      itemId: target.itemId,
      questionId: target.questionId,
      suggestionsByQuestionId,
    })
    if (result.removed) {
      seedSuggestions(result.seededSuggestions)
    }
  }, [target, suggestionsByQuestionId, seedSuggestions])

  const handleReviewDeleteRubric = useCallback(() => {
    if (!target?.itemId || !target?.questionId) return
    onDeleteRubric(target.questionId)
  }, [target, onDeleteRubric])

  const openBrowseModal = useCallback(
    async ({ rubricId, itemId, questionId } = {}) => {
      if (typeof updateRubricData !== 'function') return
      if (!itemId || !questionId) return
      setLoading(true)
      try {
        setTarget({ itemId, questionId })
        if (rubricId) {
          const fetched = await rubricsApi.getRubricsById(rubricId)
          const rubric = unwrapRubric(fetched)
          if (!rubric?.criteria?.length) {
            notification({
              type: 'warning',
              msg: 'Selected rubric has no criteria to preview.',
            })
            return
          }
          updateRubricData(rubric)
        } else {
          updateRubricData(null)
        }
        setShowBrowseModal(true)
      } catch (err) {
        notification({
          type: 'warning',
          msg: 'Unable to load rubric. Please try again.',
        })
      } finally {
        setLoading(false)
      }
    },
    [updateRubricData]
  )

  const browseModal = showBrowseModal ? (
    <GradingRubricModal
      data-cy="itemRubricBrowseModal"
      visible={showBrowseModal}
      actionType="USE EXISTING"
      toggleModal={closeBrowseModal}
      reviewBulkRubricFlow
      reviewTargetAttachedRubricId={reviewTargetAttachedRubricId}
      reviewTargetQuestion={reviewTargetQuestion}
      onBulkUseRubric={handleReviewUseRubric}
      onBulkRemoveRubric={handleReviewRemoveRubric}
      onBulkDeleteRubric={onDeleteRubric ? handleReviewDeleteRubric : undefined}
    />
  ) : null

  return { openBrowseModal, closeBrowseModal, loading, browseModal }
}

/** Eye control + item-details rubric browse modal — embeddable in status rows. */
const RubricEyeActionComponent = ({
  applied,
  updateRubricData,
  setBulkSuggestions,
  testItems,
  suggestionsByQuestionId,
}) => {
  const { openBrowseModal, loading, browseModal } = useRubricBrowseModal({
    updateRubricData,
    setBulkSuggestions,
    testItems,
    suggestionsByQuestionId,
  })

  const handleView = useCallback(() => {
    openBrowseModal({
      rubricId: applied?.rubricId,
      itemId: applied?.itemId,
      questionId: applied?.questionId,
    })
  }, [applied, openBrowseModal])

  if (!applied?.rubricId) return null

  return (
    <>
      <span>Rubric</span>
      <Tooltip title={applied.rubricName || 'View rubric'}>
        <StyledRubricActionButton
          type="button"
          data-cy="itemRubricStatusView"
          aria-label={`View rubric: ${applied.rubricName || 'View rubric'}`}
          disabled={loading}
          onClick={handleView}
        >
          <IconEye width={14} height={14} color="#158463" aria-hidden />
          VIEW
        </StyledRubricActionButton>
      </Tooltip>
      {browseModal}
    </>
  )
}

RubricEyeActionComponent.propTypes = {
  applied: PropTypes.shape({
    itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    questionId: PropTypes.string,
    rubricId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    rubricName: PropTypes.string,
  }),
  updateRubricData: PropTypes.func.isRequired,
  setBulkSuggestions: PropTypes.func.isRequired,
  testItems: PropTypes.array,
  suggestionsByQuestionId: PropTypes.object,
}

RubricEyeActionComponent.defaultProps = {
  applied: null,
  testItems: [],
  suggestionsByQuestionId: {},
}

export const RubricEyeAction = connect(
  (state) => ({
    testItems: getTestItemsSelector(state),
    suggestionsByQuestionId: getBulkSuggestionsSelector(state),
  }),
  (dispatch) => ({
    updateRubricData: (...args) => dispatch(updateRubricDataAction(...args)),
    setBulkSuggestions: (...args) =>
      dispatch(setBulkSuggestionsAction(...args)),
  })
)(RubricEyeActionComponent)
